import { Button } from "~/components/ui/button";
import headerLogoImg from "~/assets/logo.png";
import headerTextImg from "~/assets/header-text.svg";
import headerTextLightImg from "~/assets/header-text-light.svg";
import Announcement from "~/components/common/Announcement";
import { useUserStore } from "~/lib/stores/user";
import { Search } from "lucide-react";
import { BlurryBackground } from "~/components/common/BlurryBackground";
import { IoPersonOutline } from "react-icons/io5";
import Login from "~/components/common/Login";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import GetApp from "./GetApp";
import { isGuestUser } from "~/lib/user";
import SearchInput from "~/pages/deal/search/input";
import TrustpilotWidget from "~/components/common/TrustpilotWidget";
import { useGoogleOneTap } from "~/lib/hooks/useGoogleOneTap";
import { trackDownloadSelect } from "~/lib/track-events";
import { ModeSwitch } from "~/components/common/ModeSwitch";
import { cn } from "~/lib/utils";

export interface HeaderProps {
  showAnnouncement?: boolean;
  showBlidzAppMenu?: boolean;
  showSearchBar?: boolean;
  showAuthControl?: boolean;
  /** Home / Cinema. Set it on the pages that belong to that pair, not site-wide. */
  showModeSwitch?: boolean;
  /** For pages on a black background. The desktop pills stay light: they carry their own fill. */
  dark?: boolean;
}

export default function Header({
  showAnnouncement = false,
  showBlidzAppMenu = false,
  showSearchBar = true,
  showAuthControl = true,
  showModeSwitch = false,
  dark = false,
}: HeaderProps) {
  // Initialize Google One Tap when the header is mounted
  useGoogleOneTap();

  const user = useUserStore(state => state.user);
  const isLoggedIn = !!user && !isGuestUser(user);
  const [loginOpen, setLoginOpen] = useState(false);
  const [getAppOpen, setGetAppOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <BlurryBackground
      position="fixed"
      className={cn(
        "top-0 left-0 z-50 w-screen",
        !dark && "bg-gradient-to-b from-[rgba(255,251,205,0.5)] to-[rgba(255,255,255,0.5)]"
      )}
    >
      {showAnnouncement && <Announcement />}
      <div
        id="header-content"
        className="flex h-[47px] items-center justify-between gap-[8px] px-[16px] py-[10px] backdrop-blur-[10px] backdrop-filter md:h-[78px] md:gap-[24px] md:px-[24px] md:py-[16px]"
      >
        <div id="header-left-section" className="flex h-full items-center gap-[18px]">
          <Link id="header-logo-link" to="/shop" className="flex items-center gap-[6px] md:gap-[8px]">
            <div className="flex size-[24px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] md:size-[30px] md:rounded-[7.5px]">
              <img id="header-logo-icon" src={headerLogoImg} alt="Blidz" className="size-full" />
            </div>
            <img
              id="header-logo-text"
              src={dark ? headerTextLightImg : headerTextImg}
              alt="Blidz"
              className="h-[18px] w-[54px] md:h-[22px] md:w-[66px]"
            />
          </Link>
          <div className="hidden items-center rounded-[99px] border border-white bg-gradient-to-b from-[#f3f3f3] to-[#ffffff] px-[16px] py-[8px] xl:flex">
            <TrustpilotWidget
              templateId="5419b732fbfb950b10de65e5"
              height="24px"
              width="280px"
              token="26dedbac-ee82-46d3-a9b1-db69a4f5ff4b"
            />
          </div>
          {(showBlidzAppMenu || showModeSwitch) && (
            <nav
              id="header-nav"
              className="hidden h-full items-center gap-[13px] rounded-[99px] border border-white bg-gradient-to-b from-[#f3f3f3] to-[#ffffff] px-[16px] py-[8px] md:flex"
            >
              {showModeSwitch && (
                <div className="flex items-center gap-[13px]">
                  <ModeSwitch variant="desktop" />
                  {showBlidzAppMenu && <span className="h-[20px] w-px shrink-0 bg-neutral-300" />}
                </div>
              )}
              {showBlidzAppMenu && (
                <>
                  <button
                    id="header-button-blidz-app"
                    onClick={() => {
                      trackDownloadSelect();
                      setGetAppOpen(true);
                    }}
                    className="flex h-[42px] cursor-pointer items-center justify-center gap-[4px] rounded-[6px] px-[8px] py-[8px] text-[14px] leading-[20px] font-semibold text-neutral-900"
                  >
                    Blidz App
                    <svg className="size-[12px]" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="#737373"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <GetApp open={getAppOpen} onOpenChange={setGetAppOpen} />
                </>
              )}
            </nav>
          )}
        </div>

        {showModeSwitch && (
          <div id="header-mode-switch" className="md:hidden">
            <ModeSwitch variant="mobile" dark={dark} />
          </div>
        )}

        {showSearchBar && (
          <div id="header-search-container" className="hidden h-full w-full max-w-[300px] flex-1 md:ml-auto md:flex">
            <button
              id="header-search-input"
              type="button"
              onClick={() => setSearchDialogOpen(true)}
              className="flex h-full w-full cursor-pointer items-center gap-[10px] rounded-[99px] border border-white bg-gradient-to-b from-[#f3f3f3] to-[#ffffff] px-[16px] py-[8px] text-[14px] leading-[20px] font-medium text-neutral-500"
            >
              <Search id="header-search-icon" className="h-4 w-4 shrink-0 text-[#737373]" />
              Search
            </button>
          </div>
        )}

        <div
          id="header-right-section"
          className="flex h-full shrink-0 cursor-pointer items-center justify-end gap-[16px] md:max-w-[345px] md:gap-[8px]"
        >
          {showSearchBar && (
            <button
              id="header-button-search-mobile"
              type="button"
              aria-label="Search"
              onClick={() => setSearchDialogOpen(true)}
              className={cn("cursor-pointer md:hidden", dark ? "text-white" : "text-neutral-900")}
            >
              <Search className="size-[22px]" />
            </button>
          )}
          {showAuthControl && (
            <>
              {isLoggedIn && user ? (
                <div
                  id="header-avatar-container"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate("/user/profile")}
                  onKeyDown={e => e.key === "Enter" && navigate("/user/profile")}
                  className="flex items-center justify-center md:h-full md:w-[46px] md:rounded-[99px] md:border md:border-white md:bg-gradient-to-b md:from-[#f3f3f3] md:to-[#ffffff] md:px-[16px] md:py-[8px]"
                >
                  <div className="flex items-center justify-center md:size-[38px] md:rounded-[99px] md:bg-white md:p-[10px]">
                    <div className="flex size-[22px] items-center justify-center overflow-hidden rounded-[31px] bg-gradient-to-r from-[#fff6b5] to-[#fdf853] p-[10px]">
                      <p className="text-center text-[12px] leading-[18px] font-semibold text-black">
                        {user.basicInfo?.name?.[0]?.toUpperCase() || "U"}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div id="header-auth-container" className="flex h-full items-center">
                  <Button
                    id="header-button-sign-in"
                    onClick={() => setLoginOpen(true)}
                    className={cn(
                      "h-auto gap-[4px] bg-transparent p-0 text-neutral-900 shadow-none hover:bg-transparent has-[>svg]:px-0 md:flex md:h-full md:items-center md:rounded-[99px] md:border md:border-white md:bg-gradient-to-b md:from-[#f3f3f3] md:to-[#ffffff] md:px-[20px] md:py-[8px] md:text-[14px] md:leading-[20px] md:font-semibold md:has-[>svg]:px-[20px]",
                      dark && "text-white md:text-neutral-900"
                    )}
                  >
                    <IoPersonOutline className="size-[22px] md:size-[12px]" />
                    <span className="hidden md:inline">Login</span>
                  </Button>
                  <Login open={loginOpen} onOpenChange={setLoginOpen} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showSearchBar && searchDialogOpen && (
        <>
          <div
            id="header-search-mask"
            role="button"
            tabIndex={0}
            aria-label="Close search"
            className="fixed top-0 left-0 z-51 h-screen w-screen bg-black/35"
            onClick={() => setSearchDialogOpen(false)}
            onKeyDown={e => e.key === "Enter" && setSearchDialogOpen(false)}
          />
          <div
            id="search-input-container"
            className="fixed top-[16px] left-1/2 z-52 w-[80vw] max-w-[640px] -translate-x-1/2"
          >
            <SearchInput
              onSearch={keyword => {
                setSearchDialogOpen(false);
                navigate(`/shop/search?keyword=${encodeURIComponent(keyword)}`);
              }}
              onClose={() => setSearchDialogOpen(false)}
            />
          </div>
        </>
      )}
    </BlurryBackground>
  );
}
